import React, { useState } from 'react';
import { Card } from '../components/ui/Card';
import { Company } from '../types';
import { DocumentPlusIcon } from '../components/icons/DocumentPlusIcon';
import { EditIcon } from '../components/icons/EditIcon';
import { TrashIcon } from '../components/icons/TrashIcon';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { CompanyForm } from '../components/CompanyForm';
import { CompanyIcon } from '../components/icons/CompanyIcon'; // Generic icon for placeholder
import { useCompanies } from '../hooks/useCompanies';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';

interface CompanyCardProps {
  company: Company;
  onClick: () => void;
}

const CompanyCard: React.FC<CompanyCardProps> = ({ company, onClick }) => {
  return (
    <Card
      className="bg-dark-surface hover:shadow-xl transition-shadow duration-200 cursor-pointer flex flex-col justify-between min-h-[180px]" // Increased min-height
      onClick={onClick}
      titleClassName="p-0" // Remove padding from default title to allow full-width logo bg
    >
      <div className="h-20 flex items-center justify-center bg-dark-tertiary rounded-t-lg overflow-hidden">
        {company.logoUrl ? (
          <img src={company.logoUrl} alt={`${company.name} logo`} className="max-h-full max-w-full object-contain p-2" />
        ) : (
          <CompanyIcon className="text-5xl text-brand-lime" /> // Placeholder icon
        )}
      </div>
      <div className="p-3 text-center flex-grow flex flex-col justify-center">
        <h3 className="text-md font-semibold text-dark-text-primary truncate" title={company.name}>
          {company.name}
        </h3>
        <p className="text-xs text-dark-text-secondary truncate" title={`${company.address.city}, ${company.address.state}`}>
          {company.address.city}, {company.address.state}
        </p>
      </div>
    </Card>
  );
};

export const EmpresasPage: React.FC = () => {
  const { companies, addCompany, updateCompany, deleteCompany } = useCompanies();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'view' | 'edit' | 'add' | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [editingCompany, setEditingCompany] = useState<Company | Partial<Company> | null>(null);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);


  const handleOpenAddModal = () => {
    setModalMode('add');
    setSelectedCompany(null);
    setEditingCompany({});
    setIsModalOpen(true);
  };

  const handleOpenViewModal = (company: Company) => {
    setModalMode('view');
    setSelectedCompany(company);
    setEditingCompany(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (company: Company) => {
    setModalMode('edit');
    setSelectedCompany(company);
    setEditingCompany(company);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalMode(null);
    setSelectedCompany(null);
    setEditingCompany(null);
  };

  const handleSaveCompany = async (companyData: Company) => {
    if (modalMode === 'add') {
      const newCompany = {
        ...companyData,
        id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };
      await addCompany(newCompany);
      handleCloseModal();
    } else if (modalMode === 'edit' && selectedCompany) {
      await updateCompany(companyData);
      handleCloseModal();
      // Reload the application to guarantee that cascaded changes (like matching project names) are fetched globally.
      window.location.reload();
    }
  };

  const handleOpenDeleteModal = (company: Company) => {
    handleCloseModal(); // Close the view modal first
    setCompanyToDelete(company);
  };

  const handleCloseDeleteModal = () => {
    setCompanyToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (companyToDelete) {
      await deleteCompany(companyToDelete.id);
      handleCloseDeleteModal();
    }
  };
  
  const getModalTitle = () => {
    if (modalMode === 'add') return "Adicionar Nova Empresa";
    if (modalMode === 'edit' && selectedCompany) return `Editar: ${selectedCompany.name}`;
    if (modalMode === 'view' && selectedCompany) return selectedCompany.name;
    return "Detalhes da Empresa";
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-dark-text-primary">Empresas</h1>
        <Button
            variant="primary"
            size="md"
            onClick={handleOpenAddModal}
            leftIcon={<DocumentPlusIcon className="text-xl" />}
          >
            Adicionar Empresa
          </Button>
      </div>

      {companies.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {companies.map(company => (
            <CompanyCard key={company.id} company={company} onClick={() => handleOpenViewModal(company)} />
          ))}
        </div>
      ) : (
        <Card className="bg-dark-surface">
          <p className="text-dark-text-secondary text-center py-8">
            Nenhuma empresa cadastrada. Clique em "Adicionar Empresa" para começar.
          </p>
        </Card>
      )}

      {isModalOpen && (
        <Modal title={getModalTitle()} isOpen={isModalOpen} onClose={handleCloseModal} size={modalMode === 'view' ? 'md' : 'xl'}>
          {modalMode === 'view' && selectedCompany && (
            <>
              {selectedCompany.logoUrl && (
                <div className="mb-4 flex justify-center items-center bg-dark-tertiary p-2 rounded max-h-32">
                  <img 
                    src={selectedCompany.logoUrl} 
                    alt={`${selectedCompany.name} logo`} 
                    className="max-h-28 max-w-full object-contain rounded"
                  />
                </div>
              )}
              <div className="text-sm text-dark-text-secondary space-y-3 mb-6">
                <p><strong>CNPJ:</strong> {selectedCompany.cnpj || 'Não informado'}</p>
                <div>
                  <strong>Endereço:</strong>
                  <p className="ml-2">
                    {selectedCompany.address.street}, {selectedCompany.address.number || 'S/N'}
                    {selectedCompany.address.complement && `, ${selectedCompany.address.complement}`}
                    <br />
                    {selectedCompany.address.neighborhood} - {selectedCompany.address.city}, {selectedCompany.address.state}
                    <br />
                    CEP: {selectedCompany.address.zipCode}
                  </p>
                </div>
                {selectedCompany.phone && <p><strong>Telefone:</strong> {selectedCompany.phone}</p>}
                {selectedCompany.email && <p><strong>E-mail:</strong> <a href={`mailto:${selectedCompany.email}`} className="text-brand-teal hover:underline">{selectedCompany.email}</a></p>}
                {selectedCompany.website && <p><strong>Website:</strong> <a href={selectedCompany.website} target="_blank" rel="noopener noreferrer" className="text-brand-teal hover:underline">{selectedCompany.website}</a></p>}
                {selectedCompany.focalPoint && (
                  <div>
                    <strong>Ponto Focal:</strong>
                    <p className="ml-2">
                      {selectedCompany.focalPoint.name}
                      {selectedCompany.focalPoint.role && ` (${selectedCompany.focalPoint.role})`}
                      {selectedCompany.focalPoint.email && <><br />E-mail: <a href={`mailto:${selectedCompany.focalPoint.email}`} className="text-brand-teal hover:underline">{selectedCompany.focalPoint.email}</a></>}
                      {selectedCompany.focalPoint.phone && <><br />Telefone: {selectedCompany.focalPoint.phone}</>}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-dark-tertiary">
                <Button variant="danger" onClick={() => handleOpenDeleteModal(selectedCompany)} leftIcon={<TrashIcon className="text-base" />}>
                  Excluir
                </Button>
                <Button variant="primary" onClick={() => handleOpenEditModal(selectedCompany)} leftIcon={<EditIcon className="text-base" />}>
                  Editar
                </Button>
              </div>
            </>
          )}
          {(modalMode === 'add' || modalMode === 'edit') && (
            <CompanyForm
              initialCompany={editingCompany}
              onSubmit={handleSaveCompany}
              onCancel={handleCloseModal}
              isEditing={modalMode === 'edit'}
            />
          )}
        </Modal>
      )}
      {companyToDelete && (
        <DeleteConfirmationModal
            isOpen={!!companyToDelete}
            onClose={handleCloseDeleteModal}
            onConfirm={handleConfirmDelete}
            title="Confirmar Exclusão de Empresa"
            message={`Você tem certeza que deseja excluir a empresa "${companyToDelete.name}"? Esta ação não pode ser desfeita.`}
        />
      )}
    </div>
  );
};